/* Select Complete Builder — theme-only prototype. Never makes Admin API calls. */
(() => {
  const components = ['trucks','wheels','bearings','grip','hardware','risers'];
  const required = components.filter(k=>k!=='risers');
  const labels = {trucks:'Trucks (pair)',wheels:'Wheels (set of 4)',bearings:'Bearings (set)',hardware:'Hardware (set)',grip:'Griptape (sheet)',risers:'Risers (set of 2)'};
  const pickupAssemblyInstruction='Complimentary assembly available upon customer arrival. Do not preassemble.';
  const buildCartItems=(items,buildId)=>items.map(p=>({id:p.id,quantity:1,properties:{'_Select complete':buildId}}));
  const jsonHeaders={'Content-Type':'application/json','Accept':'application/json'};
  async function addCompleteToCart(items,buildId,delivery,rootUrl){
    const response=await fetch(rootUrl+'cart/add.js',{method:'POST',headers:jsonHeaders,body:JSON.stringify({items:buildCartItems(items,buildId)})});
    const result=await response.json().catch(()=>({}));
    if(!response.ok)throw new Error(result.description||result.message||'Unable to add selected components.');
    if(delivery==='pickup'){
      try{
        const attributeResponse=await fetch(rootUrl+'cart/update.js',{method:'POST',headers:jsonHeaders,body:JSON.stringify({attributes:{'Complete builder assembly':pickupAssemblyInstruction}})});
        if(!attributeResponse.ok)throw new Error('Cart attribute update failed');
      }catch(attributeError){
        const updates=Object.fromEntries((result.items||[]).map(item=>[item.key,0]));
        if(Object.keys(updates).length)await fetch(rootUrl+'cart/update.js',{method:'POST',headers:jsonHeaders,body:JSON.stringify({updates})}).catch(()=>{});
        throw new Error('Unable to save the pickup assembly instruction. Your complete was not added. Please try again.');
      }
    }
  }
  const number = x => { if (typeof x === 'number') return x; if (typeof x === 'string') { const v=parseFloat(x.replace(/[^\d.]/g,'')); return Number.isFinite(v)?v:null; } return null; };
  // Most decks store width in a product metafield. Multi-variant decks may
  // instead carry it only in the selected variant title (for example, "8.5").
  const deckWidth = deck => number(deck?.width) ?? number(deck?.variantTitle);
  const usd = cents => new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'}).format(cents/100);
  // Recommendations use Select's historical Shopify sales leaders as tie-breakers.
  // Live availability and existing width rules always take precedence.
  // This is a curated snapshot, NOT an automatically refreshed Shopify sales feed.
  const popularity = (p, kind) => {
    const t = (p.title || '').toLowerCase();
    if (kind === 'trucks') {
      if (/independent.*stage 11.*polished/.test(t)) return 100;
      if (/thunder.*team editions.*polished/.test(t)) return 87;
      if (/thunder.*light polished/.test(t)) return 83;
      if (/independent.*forged hollow/.test(t)) return 78;
      if (/venture.*hi polished/.test(t)) return 70;
      if (/ace.*af1.*polished/.test(t)) return 65;
    }
    if (kind === 'wheels') {
      if (/spitfire.*formula 4.*conical full/.test(t)) return 100;
      if (/spitfire.*formula 4.*og classics/.test(t)) return 92;
      if (/spitfire.*formula 4/.test(t)) return 85;
      if (/essentials.*52mm/.test(t)) return 72;
      if (/spitfire.*classics/.test(t)) return 70;
    }
    if (kind === 'bearings') {
      if (/bones.*reds/.test(t)) return 100;
      if (/spitfire.*cheapshots/.test(t)) return 90;
      if (/bones.*swiss/.test(t)) return 87;
      if (/modus/.test(t)) return 72;
      if (/bronson/.test(t)) return 67;
    }
    if (kind === 'hardware') {
      if (/incometaxes.*1\s*(?:in|inch).*phillips.*black/.test(t)) return 100;
      if (/shorty.*1\s*(?:in|inch).*phillips/.test(t)) return 95;
      if (/incometaxes.*1\s*(?:in|inch).*phillips/.test(t)) return 85;
    }
    if (kind === 'grip') {
      if (/mob.*black/.test(t)) return 100;
      if (/jessup.*black/.test(t)) return 90;
    }
    return 25;
  };
  const bad = (p, kind) => {
    const s=((p.title || '')+' '+(p.vendor || '')).toLowerCase();
    if(kind==='grip') return !(/\b(jessup|mob)\b/.test(s) && /\bblack\b/.test(s)) || /\b(roll|box|100 sheets|20 sheets|pack of|case)\b/.test(s);
    if(kind==='hardware') {
      // Must be explicitly 1 inch; rejects 1 1/4, 1.25, 7/8, and 1.5 inch.
      const oneInch = /\b1\s*(?:inch|in\.?\b|[\"”])/.test(s) && !/\b1\s*(?:1\s*\/\s*4|\.25|1\s*\/\s*2|\.5)/.test(s);
      return !oneInch || !/\bphillips\b/.test(s) || /\b(bundle|kit|bulk|box|riser)\b/.test(s);
    }
    if(kind==='trucks') return /\b(bundle|kit|just add wood|complete|bushings|pivot cups|baseplate)\b/.test(s);
    if(kind==='wheels') return /\b(bundle|kit|complete|beanie|hat|bearing)\b/.test(s);
    return /\b(bundle|kit|complete)\b/.test(s);
  };
  const eligible = (products,kind,deck) => products.filter(p => {
    if(!p.available || !(p.price>0) || bad(p,kind))return false;
    if(kind==='trucks') {const w=deckWidth(deck),f=number(p.fit); return w!==null && f!==null && Math.abs(w-f)<=0.13;}
    if(kind==='wheels'){const w=number(p.wheel);return w!==null&&w>=52&&w<=54;}
    return true;
  }).sort((a,b)=>a.price-b.price);
  // Wheel-clearance guidance is approximate; actual clearance also depends on truck height.
  const riserTarget = wheel => {const mm=number(wheel?.wheel);return mm!==null&&mm>=60?0.25:mm!==null&&mm>=56?0.125:0;};
  const thickness = p => {
    const t=(p?.title||'').toLowerCase();
    const mixed=t.match(/(\d+)\s+(\d+)\s*\/\s*(\d+)\s*(?:inches|inch|in\b|["”])/);
    if(mixed)return Number(mixed[1])+Number(mixed[2])/Number(mixed[3]);
    const frac=t.match(/(\d+)\s*\/\s*(\d+)\s*(?:inches|inch|in\b|["”])/);
    if(frac)return Number(frac[1])/Number(frac[2]);
    const dec=t.match(/(\d+(?:\.\d+)?)\s*(?:inches|inch|in\b|["”])/);
    if(dec)return Number(dec[1]);
    const mm=t.match(/(\d+(?:\.\d+)?)\s*mm\b/);return mm?Number(mm[1])/25.4:null;
  };
  const hardwareLength=p=>{
    const t=(p?.title||'').toLowerCase();
    const mixed=t.match(/(\d+)\s+(\d+)\s*\/\s*(\d+)\s*(?:inches|inch|in\b|["”])/);
    if(mixed)return Number(mixed[1])+Number(mixed[2])/Number(mixed[3]);
    const frac=t.match(/(\d+)\s*\/\s*(\d+)\s*(?:inches|inch|in\b|["”])/);
    if(frac)return Number(frac[1])/Number(frac[2]);
    const dec=t.match(/(\d+(?:\.\d+)?)\s*(?:inches|inch|in\b|["”])/);return dec?Number(dec[1]):null;
  };
  // Hardware should be an intentional quality choice, not merely the cheapest bolt set.
  // The generic fully threaded Shorty's set is an entry-level recommendation only.
  // The title is insufficient to prove shank design for other brands; these are
  // merchant-approved model preferences, not a claim about every fastener's geometry.
  const hardwareTierScore=(p,tier)=>{
    const t=(p.title||'').toLowerCase();
    const genericShortys=/shorty[’']?s?\s+hardware\b/.test(t) && !/silverado|lights|longneck/.test(t);
    if(tier==='good'){
      if(genericShortys)return 160;
      if(/incometaxes.*phillips/.test(t))return 125;
      if(/modus.*phillips/.test(t))return 100;
      return 50;
    }
    if(genericShortys || /shorty[’']?s?/.test(t))return -10000;
    if(tier==='better'){
      if(/spitfire.*phillips/.test(t))return 160;
      if(/ace.*phillips/.test(t))return 150;
      if(/girl.*longneck.*phillips/.test(t))return 130;
      if(/chocolate.*phillips/.test(t))return 115;
      if(/slappy.*grip tip.*phillips/.test(t))return 110;
      return 60;
    }
    if(/girl.*longneck.*phillips/.test(t))return 180;
    if(/slappy.*grip tip.*phillips/.test(t))return 165;
    if(/spitfire.*phillips/.test(t))return 155;
    if(/ace.*phillips/.test(t))return 145;
    if(/chocolate.*phillips/.test(t))return 130;
    return 60;
  };
  const chooseHardware=(items,target,tier='better')=>{
    const requiredLength=target>0?1+target:1;
    const possibles=items.filter(p=>p.available&&p.price>0&&/phillips/i.test(p.title||'')&&!/\b(bulk|box|bundle|riser)\b/i.test(p.title||'')&&hardwareTierScore(p,tier)>-1000);
    const matches=possibles.filter(p=>{const l=hardwareLength(p);return l!==null&&l>=requiredLength-0.001&&l<=requiredLength+0.25;});
    const pool=matches.length?matches:possibles;
    return [...pool].sort((a,b)=>{
      const diffA=Math.abs((hardwareLength(a)??9)-requiredLength);
      const diffB=Math.abs((hardwareLength(b)??9)-requiredLength);
      return diffA-diffB||hardwareTierScore(b,tier)-hardwareTierScore(a,tier)||a.price-b.price;
    })[0]||null;
  };
  const chooseRiser=(items,target)=>target?([...items].filter(p=>p.available&&p.price>0&&thickness(p)!==null).sort((a,b)=>Math.abs(thickness(a)-target)-Math.abs(thickness(b)-target)||a.price-b.price)[0]||null):null;
  const bands = {
    trucks:{good:[3000,5400],better:[5000,6200],best:[5800,9500]},
    wheels:{good:[1800,3300],better:[3000,4500],best:[4300,7500]},
    bearings:{good:[800,1600],better:[1600,3000],best:[3000,11000]}
  };
  const pick = (items,tier,kind) => {
    if(!items.length)return null;
    if(kind==='hardware') return chooseHardware(items,0,tier);
    if(kind==='grip') {
      // Only classic plain-black MOB or Jessup sheets may be preset defaults.
      // Expensive graphic/colored grip is browsable, but NEVER a tier upgrade.
      const regular=items.filter(p=>/^(?:mob griptape black|jessup grip tape black)$/i.test((p.title||'').trim()));
      const preferred=tier==='good'?'jessup':'mob';
      const prioritized=regular.find(p=>p.title.toLowerCase().startsWith(preferred));
      return prioritized||regular[0]||null;
    }
    if(kind==='wheels' && (tier==='better'||tier==='best')) {
      // Merchant-curated defaults: Better = standard Spitfire Classics;
      // Best = Spitfire Formula Four Classics (not OG Classics or Conical Full).
      // Preserve location filtering: `items` has already been scoped by suggested().
      const isSpitfire = p => /spitfire/i.test((p.vendor||'')+' '+(p.title||''));
      const isFormula4 = p => /formula\s*(?:4|four)|\bf4\b/i.test(p.title||'');
      const isClassicShape = p => /\bclassics?\b/i.test(p.title||'') && !/\bog\s+classics?\b|conical|radial|soft\s+slider|sapphire|lock.?in/i.test(p.title||'');
      const desired = p => isSpitfire(p) && isClassicShape(p) && (tier==='best' ? isFormula4(p) : !isFormula4(p));
      // Fallback keeps the desired urethane tier even if its Classic shape is
      // unavailable, then other Spitfire wheels, then an eligible budget wheel.
      const modelGroup = p => desired(p) ? 0
        : isSpitfire(p) && (tier==='best' ? isFormula4(p) : !isFormula4(p)) ? 1
        : isSpitfire(p) ? 2 : 3;
      const durometer = p => {
        const raw=String(p.hardness||'').toUpperCase();
        const title=String(p.title||'').toUpperCase();
        const match=(raw.match(/\b(\d{2,3})\s*A\b/)||title.match(/\b(\d{2,3})\s*A\b/));
        return match ? Number(match[1]) : null;
      };
      const hardnessRank = p => durometer(p)===99 ? 0 : durometer(p)===101 ? 1 : 2;
      const sizeRank = p => {const mm=number(p.wheel);return mm===53?0:mm===54?1:mm===52?2:3;};
      return [...items].sort((a,b)=>
        modelGroup(a)-modelGroup(b) || hardnessRank(a)-hardnessRank(b) ||
        sizeRank(a)-sizeRank(b) || popularity(b,'wheels')-popularity(a,'wheels') || a.price-b.price
      )[0];
    }
    const band=bands[kind]?.[tier];
    if(!band)return items[0];
    const inBand=items.filter(p=>p.price>=band[0]&&p.price<=band[1]);
    const pool=inBand.length?inBand:items;
    const midpoint=(band[0]+band[1])/2;
    return [...pool].sort((a,b)=>{
      const score=p=>popularity(p,kind)-Math.abs(p.price-midpoint)/150;
      return score(b)-score(a)||a.price-b.price;
    })[0];
  };
  document.querySelectorAll('.scb[data-builder-id]').forEach(root => {
    let data; try { data=JSON.parse(root.querySelector('[data-scb-data]').textContent); } catch(e) { console.error('Select builder catalog JSON invalid',e); return; }
    const $ = s => root.querySelector(s);
    const deckSelect=$('[data-scb-deck]'), tierSelect=$('[data-scb-tier]'), fields=$('[data-scb-fields]'), summary=$('[data-scb-summary]'), total=$('[data-scb-total]'), add=$('[data-scb-add]'), error=$('[data-scb-error]');
    const modal=$('[data-scb-modal]'), results=$('[data-scb-results]'), search=$('[data-scb-search]'), sort=$('[data-scb-sort]');
    const brandList=$('[data-scb-brand-list]'),widthList=$('[data-scb-width-list]');
    const selectedBrands=new Set(), selectedWidths=new Set(), selectedWheelSizes=new Set(), selectedWheelHardnesses=new Set(), selectedTruckFits=new Set();
    const catalog={},selected={},manual={}; let deck=null,modalKind='decks',lastFocus=null;
    const productPageSize=window.matchMedia('(max-width: 650px)').matches?24:48;
    let visibleProductCount=productPageSize,recommendationsReady=false;
    let delivery='shipping';
    const eligibleForDelivery=p=>delivery==='shipping'||p?.pickup===true;
    // Internal routing preference derived from merchant-maintained custom.locations.
    // This is a merchandising classification, NOT real-time location stock proof.
    // Shopify's variant availability and checkout remain authoritative for orders.
    const stockedAt=(p,loc)=>{
      const values=Array.isArray(p?.locations)?p.locations:[];
      if(loc==='westheimer')return values.includes('In Store and Online')||values.includes('In Store Only');
      if(loc==='warehouse')return values.includes('Online Only');
      return false;
    };
    const shippingLocations=['westheimer','warehouse'];
    const preferredShippingLocation=()=>{
      if(delivery!=='shipping'||!deck)return null;
      const available=shippingLocations.filter(loc=>stockedAt(deck,loc));
      if(!available.length)return null;
      if(available.length===1)return available[0];
      // When the deck can ship from either location, favor the one with the most
      // rules-eligible components; break ties by the stronger tier recommendations.
      const score=loc=>required.reduce((total,k)=>{
        const pool=eligible(data[k]||[],k,deck).filter(p=>stockedAt(p,loc));
        return total+(pool.length?100+popularity(pick(pool,tierSelect.value,k),k):0);
      },0);
      return [...available].sort((a,b)=>score(b)-score(a))[0];
    };
    const matchingLocation=(items,loc)=>{
      if(delivery!=='shipping'||!loc)return items;
      const match=items.filter(p=>stockedAt(p,loc));
      return match.length?match:items;
    };
    const suggested=(items,tier,kind,loc=preferredShippingLocation())=>{
      const available=items.filter(eligibleForDelivery);
      if(kind==='grip'&&delivery==='shipping'&&loc==='warehouse') {
        // Select merchandising rule: Warehouse-sourced deck -> standard Jessup
        // Black for ALL tiers. Its metafield says In Store and Online, although
        // Shopify currently also holds stock at Warehouse. The metafield alone
        // cannot guarantee this stock in real time; verify before publishing.
        return available.find(p=>/^jessup grip tape black$/i.test((p.title||'').trim()))||null;
      }
      return pick(matchingLocation(available,loc),tier,kind);
    };
    // We intentionally DO NOT infer split fulfillment from custom.locations.
    // That metafield describes sales/fulfillment eligibility rather than all
    // inventory locations: Jessup Black, for example, is stocked at both the
    // retail shop and warehouse despite its 'In Store and Online' classification.
    // A split-package notice may only be shown after verified per-location
    // inventory for every selected variant is securely available. Until then,
    // suppress this message instead of making a potentially false claim.
    const selectedPickupIssues=()=>[...(deck?[['Deck',deck]]:[]),...components.map(k=>[labels[k],current(k)]).filter(([,p])=>p)].filter(([,p])=>!p.pickup).map(([label])=>label);
    const kindKeys=['trucks','wheels','bearings','grip','hardware','risers'];
    const handles=(root.dataset.scbComponentCollections||'').split(',').map(x=>x.trim());
    const componentHandles=Object.fromEntries(kindKeys.map((k,i)=>[k,handles[i]||'']));
    const loadedComponents=new Set(), componentPending={};
    let decksLoaded=false, decksLoading=null, deckLoadError=null;
    const deckCollection=root.dataset.scbDeckCollection;
    // Shopify's section-rendering endpoint preserves the Liquid metafields used by the matcher.
    async function fetchDeckPage(page){
      const base=window.Shopify?.routes?.root||'/';
      const path=base.replace(/\/$/,'')+'/collections/'+encodeURIComponent(deckCollection);
      const url=new URL(path,window.location.origin);
      url.searchParams.set('sort_by','created-descending');
      url.searchParams.set('page',String(page));
      url.searchParams.set('section_id','select-builder-deck-catalog');
      // Needed when a merchant is previewing an unpublished theme.
      for(const key of ['preview_theme_id','_fd','pb']){
        const value=new URLSearchParams(window.location.search).get(key);
        if(value)url.searchParams.set(key,value);
      }
      const response=await fetch(url.toString(),{headers:{'Accept':'text/html'}});
      if(!response.ok)throw new Error('Shopify could not load the deck catalog (HTTP '+response.status+').');
      const html=await response.text();
      const doc=new DOMParser().parseFromString(html,'text/html');
      const el=doc.querySelector('[data-scb-paged-decks]');
      if(!el)throw new Error('Shopify deck catalog section is missing. Make sure select-builder-deck-catalog.liquid was saved.');
      const payload=JSON.parse(el.textContent);
      if(!Array.isArray(payload.products)||!Number.isInteger(payload.pages)||payload.pages<1)
        throw new Error('Shopify returned an invalid deck page.');
      return payload;
    }
    async function loadDecks(){
      if(decksLoaded||decksLoading)return decksLoading;
      if(!deckCollection){deckLoadError='No deck collection is selected in the theme editor.';renderProducts();return;}
      decksLoading=(async()=>{
        deckLoadError=null;
        const first=await fetchDeckPage(1);
        const pages=first.pages;
        if(pages>100)throw new Error('Deck collection is too large to load in this version.');
        const ordered=[first];
        // Load additional pages in small batches; avoid overloading storefront requests.
        for(let start=2;start<=pages;start+=3){
          const batch=[];for(let i=start;i<Math.min(start+3,pages+1);i++)batch.push(fetchDeckPage(i));
          ordered.push(...await Promise.all(batch));
          if(!modal.hidden&&modalKind==='decks'){$('[data-scb-count]').textContent='Loading decks… '+Math.min(start+2,pages)+' of '+pages+' pages';}
        }
        const seen=new Set(),products=[];
        for(const payload of ordered){
          for(const item of payload.products){if(!seen.has(String(item.id))){seen.add(String(item.id));products.push(item);}}
        }
        data.decks=products;
        // Rebuild the backing native select so all loaded decks can be chosen.
        const previous=deckSelect.value;
        deckSelect.replaceChildren(new Option('Select a deck',''));
        for(const item of data.decks)deckSelect.add(new Option(item.title,String(item.id)));
        if(previous)deckSelect.value=previous;
        decksLoaded=true;
        // A product-page link may refer to a deck outside the first 50 products.
        // Apply the requested deck only after the complete catalog has loaded.
        const wantedVariant=params.get('variant');
        const wantedHandle=params.get('deck');
        if(wantedVariant||wantedHandle){
          const requested=data.decks.find(d=>(wantedVariant&&String(d.id)===wantedVariant)||(wantedHandle&&d.handle===wantedHandle));
          if(requested&&String(deckSelect.value)!==String(requested.id)){
            deckSelect.value=String(requested.id);
            for(const k of components){selected[k]=null;manual[k]=false;}
            refresh();
          }
        }
        if(!modal.hidden&&modalKind==='decks')renderFilterOptions();
      })().catch(e=>{deckLoadError=e.message||'Unable to load decks.';console.error('Select complete builder deck loading:',e);})
        .finally(()=>{decksLoading=null;if(!modal.hidden&&modalKind==='decks')renderProducts();});
      return decksLoading;
    }
    // Fast product-page deep links: fetch only the requested deck from its
    // Shopify product URL, preserving deck metafields and variant availability.
    // Full collection pagination stays lazy until the customer browses decks.
    async function loadLinkedDeck(){
      const wantedHandle=params.get('deck');
      const wantedVariant=params.get('variant');
      if(!wantedHandle){
        // Legacy variant-only links still use the full catalog.
        if(wantedVariant)await loadDecks();
        return;
      }
      const button=$('[data-scb-deck-button]');
      button.setAttribute('aria-busy','true');
      button.replaceChildren(node('span','scb__deck-placeholder','Preparing your complete…'),
        node('span','scb__change','Loading your selected deck'));
      try{
        const base=(window.Shopify?.routes?.root||'/').replace(/\/$/,'');
        const url=new URL(base+'/products/'+encodeURIComponent(wantedHandle),window.location.origin);
        url.searchParams.set('section_id','select-builder-single-deck');
        if(wantedVariant)url.searchParams.set('variant',wantedVariant);
        for(const key of ['preview_theme_id','_fd','pb']){
          const v=params.get(key);if(v)url.searchParams.set(key,v);
        }
        const response=await fetch(url.toString(),{headers:{Accept:'text/html'}});
        if(!response.ok)throw new Error('Deck lookup HTTP '+response.status);
        const documentHTML=new DOMParser().parseFromString(await response.text(),'text/html');
        const element=documentHTML.querySelector('[data-scb-single-deck]');
        if(!element)throw new Error('Single-deck helper not found.');
        const item=JSON.parse(element.textContent);
        if(!item||!item.available||item.handle!==wantedHandle)throw new Error('Requested deck is unavailable.');
        // Respect a selected variant when present. Liquid resolves the current
        // available variant from the requested product URL.
        if(wantedVariant&&String(item.id)!==wantedVariant)throw new Error('Requested variant is unavailable.');
        if(!data.decks.some(d=>String(d.id)===String(item.id)))data.decks.unshift(item);
        if(![...deckSelect.options].some(o=>o.value===String(item.id)))
          deckSelect.add(new Option(item.title,String(item.id)));
        deckSelect.value=String(item.id);
        for(const k of components){selected[k]=null;manual[k]=false;}
        refresh();
      }catch(e){
        console.warn('Select builder direct deck lookup failed; falling back to collection:',e);
        await loadDecks();
        if(!deck){
          error.textContent='We could not automatically find that deck. Please choose it from Browse decks.';
          error.hidden=false;
        }
      }finally{
        button.removeAttribute('aria-busy');
        if(!deck)renderDeck();
      }
    }
    async function loadComponents(kind){
      if(loadedComponents.has(kind)||componentPending[kind])return componentPending[kind];
      const handle=componentHandles[kind];if(!handle)return;
      componentPending[kind]=(async()=>{
        const base=window.Shopify?.routes?.root||'/';
        async function page(n){
          const url=new URL(base.replace(/\/$/,'')+'/collections/'+encodeURIComponent(handle),window.location.origin);
          url.searchParams.set('page',String(n));url.searchParams.set('section_id','select-builder-component-catalog');
          for(const key of ['preview_theme_id','_fd','pb']){const v=new URLSearchParams(location.search).get(key);if(v)url.searchParams.set(key,v);}
          const res=await fetch(url,{headers:{Accept:'text/html'}});if(!res.ok)throw Error('HTTP '+res.status);
          const doc=new DOMParser().parseFromString(await res.text(),'text/html');const tag=doc.querySelector('[data-scb-paged-components]');
          if(!tag)throw Error('Component catalog helper not found');return JSON.parse(tag.textContent);
        }
        const first=await page(1);if(first.pages>100)throw Error('Component catalog exceeds 100 pages');
        const pages=[first];for(let n=2;n<=first.pages;n+=3){const tasks=[];for(let i=n;i<=Math.min(n+2,first.pages);i++)tasks.push(page(i));pages.push(...await Promise.all(tasks));}
        const seen=new Set();data[kind]=pages.flatMap(pg=>pg.products).filter(p=>{if(seen.has(String(p.id)))return false;seen.add(String(p.id));return true;});
        loadedComponents.add(kind);
        if(!modal.hidden&&modalKind===kind){renderFilterOptions();renderProducts();}
      })().catch(e=>{console.error('Select builder could not load '+kind,e);if(!modal.hidden&&modalKind===kind){$('[data-scb-count]').textContent='Could not load complete catalog; showing initial products.';}}).finally(()=>{delete componentPending[kind];});
      return componentPending[kind];
    }
    const imageAt=(source,width)=>{try{const url=new URL(source,location.origin);url.searchParams.set('width',String(width));return url.toString();}catch(e){return source;}};
    const img=(p,klass='',width=240)=>{const picture=document.createElement('div');picture.className='scb__photo '+klass;if(p?.image){const el=document.createElement('img');el.src=imageAt(p.image,width);el.alt=p.title||'';el.loading='lazy';el.decoding='async';picture.append(el);}else{picture.textContent='SELECT';}return picture;};
    const node=(tag,klass,text)=>{const el=document.createElement(tag);if(klass)el.className=klass;if(text!==undefined)el.textContent=text;return el;};
    const priced=p=>p?usd(p.price):'Unavailable';
    const all=(kind)=>(kind==='decks'?data.decks:(data[kind]||[])).filter(p=>p.available&&p.price>0&&eligibleForDelivery(p));
    const tiers=[['good','GOOD','Value setup','Entry-level essentials'],['better','BETTER','Everyday favorite','Popular upgrades'],['best','BEST','Premium setup','Higher-end components']];
    const params=new URLSearchParams(location.search);
    for(const d of data.decks){const o=document.createElement('option');o.value=String(d.id);o.textContent=d.title;deckSelect.append(o);}
    const pre=data.decks.find(d=>(params.get('variant')&&String(d.id)===params.get('variant'))||(params.get('deck')&&d.handle===params.get('deck')));
    if(pre)deckSelect.value=String(pre.id);
    const current=k=>k==='decks'?deck:(data[k]||[]).find(p=>String(p.id)===String(selected[k]));
    function reconcileWheelDependencies(){
      const target=riserTarget(current('wheels'));
      if(!manual.risers)selected.risers=chooseRiser(matchingLocation((data.risers||[]).filter(eligibleForDelivery),preferredShippingLocation()),target)?.id||null;
      if(!manual.hardware)selected.hardware=chooseHardware(matchingLocation((data.hardware||[]).filter(eligibleForDelivery),preferredShippingLocation()),current('risers')?thickness(current('risers'))||target:0,tierSelect.value)?.id||null;
    }
    const getBuild=tier=>{
      if(!deck)return [];
      const parts=Object.fromEntries(required.map(k=>[k,suggested(catalog[k]||[],tier,k)]));
      const target=riserTarget(parts.wheels);parts.risers=chooseRiser(matchingLocation((data.risers||[]).filter(eligibleForDelivery),preferredShippingLocation()),target);parts.hardware=chooseHardware(matchingLocation((data.hardware||[]).filter(eligibleForDelivery),preferredShippingLocation()),parts.risers?thickness(parts.risers)||target:0,tier);
      return [deck,...components.map(k=>parts[k])];
    };
    function renderTiers(){
      const container=$('[data-scb-tier-cards]');container.replaceChildren();
      tiers.forEach(([key,title,subtitle,desc])=>{
        const b=node('button','scb__tier'+(tierSelect.value===key?' is-active':''));b.type='button';b.setAttribute('aria-pressed',String(tierSelect.value===key));
        b.append(node('span','scb__tier-name',title),node('span','scb__tier-sub',subtitle),node('span','scb__tier-desc',desc));
        const vals=getBuild(key);if(!recommendationsReady&&deck){b.append(node('span','scb__tier-price','Preparing…'));}else if(vals.length&&vals.slice(0,-1).every(Boolean)){b.append(node('strong','scb__tier-price',usd(vals.reduce((n,p)=>n+(p?.price||0),0))));}else{b.append(node('span','scb__tier-price','Select deck first'));}
        b.addEventListener('click',()=>{tierSelect.value=key;if(recommendationsReady){for(const k of required){if(!manual[k])selected[k]=suggested(catalog[k]||[],key,k)?.id||null;}reconcileWheelDependencies();}render();});container.append(b);
      });
      const detail=$('[data-scb-tier-detail]');
      if(!deck)detail.textContent='Choose a deck to compare the actual parts in each setup.';
      else if(!recommendationsReady)detail.textContent='Preparing stable recommendations from the full component catalog…';
      else {const build=getBuild(tierSelect.value);const names=[build[1]?.title,build[2]?.title,build[3]?.title].filter(Boolean);detail.textContent=`${tierSelect.options[tierSelect.selectedIndex].text} setup: ${names.join(' · ')}`;}
    }
    function renderDeck(){const b=$('[data-scb-deck-button]');b.replaceChildren();if(!deck){b.append(node('span','scb__deck-placeholder','Choose a skateboard deck to begin'),node('span','scb__change','Browse decks →'));return;}b.append(img(deck,'scb__deck-image',360));const details=node('span','scb__deck-details');const width=deckWidth(deck);details.append(node('strong','',deck.title),node('small','',`Deck width: ${width??'Not specified'}" · ${priced(deck)}`));b.append(details,node('span','scb__change','Change →'));}
    function renderParts(){fields.replaceChildren();for(const k of components){const p=current(k),row=node('div','scb__part');row.append(img(recommendationsReady?p:null));const d=node('div','scb__part-info');d.append(node('span','scb__part-type',labels[k]),node('strong','scb__part-title',!recommendationsReady&&deck?'Preparing recommendation…':(p?.title||(k==='risers'?'Not needed for your current wheel size':'No recommended component'))),node('span','scb__part-price',!recommendationsReady&&deck?'—':(p?priced(p):(k==='risers'?'$0.00':'Unavailable'))));row.append(d);const btn=node('button','scb__change-button',k==='risers'&&!p?'Add / Change':'Change');btn.type='button';btn.disabled=!deck||!recommendationsReady;btn.addEventListener('click',()=>openModal(k));row.append(btn);
      if(k==='risers'&&p){const remove=node('button','scb__change-button','Remove');remove.type='button';remove.addEventListener('click',()=>{manual.risers=true;selected.risers=null;reconcileWheelDependencies();render();});row.append(remove);}
      if(k==='trucks'&&p&&Math.abs((deckWidth(deck)||0)-(number(p.fit)||0))>0.13)d.append(node('small','scb__guidance','Outside typical deck/truck width matching; selected by you.'));
      if(k==='wheels'&&riserTarget(p)>0){
        const riser=current('risers'), hardware=current('hardware');
        const riserSize=riser?thickness(riser):null;
        const neededLength=1+(riserSize||riserTarget(p));
        const hardwareSize=hardware?hardwareLength(hardware):null;
        const longerHardware=hardwareSize!==null&&hardwareSize>=neededLength-0.001;
        let guidance;
        if(manual.risers&&!riser){
          guidance="You've chosen to ride larger wheels without risers. Depending on your trucks, you may experience wheel bite. You can add risers below at any time.";
        }else if(!riser||!longerHardware){
          guidance='Larger wheels may require risers and longer hardware. We could not confirm both suitable selections; please review the options below.';
        }else if(!manual.risers&&!manual.hardware){
          guidance="Larger wheels selected. We've automatically added recommended risers and longer hardware to your build below. You can change or remove either.";
        }else if(manual.risers&&!manual.hardware){
          guidance="We've recommended longer hardware for your chosen risers. Please review your setup below.";
        }else if(!manual.risers&&manual.hardware){
          guidance="We've added recommended risers for your larger wheels. Your hardware selection has been kept; please review its length below.";
        }else{
          guidance='Larger wheels may need additional clearance. Please review your chosen risers and hardware below.';
        }
        d.append(node('small','scb__guidance',guidance));
      }
      fields.append(row);}}
    function renderSummary(){
      const items=[deck,...components.map(current)];let cents=0;
      const complete=Boolean(deck)&&required.every(k=>current(k));
      const pickupIssues=delivery==='pickup'?selectedPickupIssues():[];
      summary.replaceChildren();
      if(!deck){summary.textContent='Select a deck to begin.';}
      else {for(let i=0;i<items.length;i++){
        const p=items[i],r=node('div','scb__summary-line');
        r.append(node('span','',i===0?'Deck':labels[components[i-1]]),node('strong','',p?usd(p.price):(i===items.length-1?'Not needed — $0.00':'Not available')));
        summary.append(r);if(p)cents+=p.price;
      }}
      total.textContent=!recommendationsReady&&deck?'—':usd(cents);
      const count=items.filter(Boolean).length;
      $('[data-scb-selected-count]').textContent=!deck?'Select a deck to begin':(!recommendationsReady?'Preparing stable recommendations…':`${count} ${count===1?'component':'components'} selected`);
      const pickupWarning=$('[data-scb-pickup-warning]');
      pickupWarning.hidden=!(delivery==='pickup'&&pickupIssues.length);
      pickupWarning.textContent=pickupWarning.hidden?'':`Not currently available for in-store pickup: ${pickupIssues.join(', ')}. Choose in-store options or switch to shipping; we won't replace your selections automatically.`;
      const existing=$('[data-scb-delivery-warning]');if(existing)existing.remove();
      if(delivery==='pickup'&&pickupIssues.length){
        const warn=node('p','scb__delivery-warning',`Not currently available for in-store pickup: ${pickupIssues.join(', ')}. Choose in-store options or switch to shipping; we won't replace your selections automatically.`);
        warn.setAttribute('data-scb-delivery-warning','');summary.append(warn);
      }
      add.disabled=!recommendationsReady||!complete||pickupIssues.length>0;
      $('[data-scb-summary-delivery]').textContent=delivery==='pickup'?'Free pickup at Select Skate Shop. Assembly available on request when you arrive.':'Free US shipping on qualifying $100+ orders.';
    }
    function render(){renderDeck();renderTiers();renderParts();renderSummary();}
    function refresh(){deck=data.decks.find(d=>String(d.id)===deckSelect.value)||null;for(const k of components){catalog[k]=deck?eligible(data[k]||[],k,deck).filter(eligibleForDelivery):[];if(!manual[k])selected[k]=k==='risers'?null:suggested(catalog[k],tierSelect.value,k)?.id||null;}if(deck)reconcileWheelDependencies();render();}
    function closeModal(){modal.hidden=true;document.body.classList.remove('scb__modal-open');lastFocus?.focus();}
    const widthKey = value => {const w=number(value);return w===null?null:String(Number(w.toFixed(3)));};
    function renderFilterOptions(){
      const products=all(modalKind);
      const vendors=[...new Set(products.map(p=>(p.vendor||'').trim()).filter(Boolean))].sort((a,b)=>a.localeCompare(b));
      const possibleBrands=new Set(vendors);for(const item of [...selectedBrands])if(!possibleBrands.has(item))selectedBrands.delete(item);
      brandList.replaceChildren();
      for(const vendor of vendors){
        const label=node('label','scb__check-option');const check=document.createElement('input');check.type='checkbox';check.value=vendor;check.checked=selectedBrands.has(vendor);
        check.addEventListener('change',()=>{check.checked?selectedBrands.add(vendor):selectedBrands.delete(vendor);visibleProductCount=productPageSize;updateFilterSummaries();renderProducts();});
        label.append(check,node('span','',vendor));brandList.append(label);
      }
      const isDeck=modalKind==='decks';$('[data-scb-width-field]').hidden=!isDeck;
      widthList.replaceChildren();
      if(isDeck){
        const values=[...new Set(products.map(p=>widthKey(deckWidth(p))).filter(v=>v!==null))].sort((a,b)=>Number(a)-Number(b));
        const possibleWidths=new Set(values);for(const item of [...selectedWidths])if(!possibleWidths.has(item))selectedWidths.delete(item);
        for(const value of values){
          const label=node('label','scb__check-option');const check=document.createElement('input');check.type='checkbox';check.value=value;check.checked=selectedWidths.has(value);
          check.addEventListener('change',()=>{check.checked?selectedWidths.add(value):selectedWidths.delete(value);visibleProductCount=productPageSize;updateFilterSummaries();renderProducts();});
          label.append(check,node('span','',value+'"'));widthList.append(label);
        }
      }
      const extras=[
        {kind:'wheels',field:'wheel-size',set:selectedWheelSizes,prop:'wheel',unit:'mm'},
        {kind:'wheels',field:'wheel-hardness',set:selectedWheelHardnesses,prop:'hardness',unit:''},
        {kind:'trucks',field:'truck-fit',set:selectedTruckFits,prop:'fit',unit:'"'}
      ];
      for(const spec of extras){
        const visible=modalKind===spec.kind;
        $(`[data-scb-${spec.field}-field]`).hidden=!visible;
        const list=$(`[data-scb-${spec.field}-list]`);list.replaceChildren();
        if(!visible)continue;
        const norm=v=>spec.prop==='hardness'?String(v??'').trim().toUpperCase():widthKey(v);
        const values=[...new Set(products.map(p=>norm(p[spec.prop])).filter(Boolean))].sort((a,b)=>spec.prop==='hardness'?a.localeCompare(b,undefined,{numeric:true}):Number(a)-Number(b));
        for(const v of [...spec.set])if(!values.includes(v))spec.set.delete(v);
        for(const v of values){
          const label=node('label','scb__check-option');const check=document.createElement('input');check.type='checkbox';check.value=v;check.checked=spec.set.has(v);
          check.addEventListener('change',()=>{check.checked?spec.set.add(v):spec.set.delete(v);visibleProductCount=productPageSize;updateFilterSummaries();renderProducts();});
          label.append(check,node('span','',v+spec.unit));list.append(label);
        }
      }
      updateFilterSummaries();
    }
    function updateFilterSummaries(){
      const summaries=(set,base)=>set.size?([...set].length===1?[...set][0]+(base==='widths'?'"':''):`${set.size} ${base} selected`):('All '+base);
      $('[data-scb-brand-summary]').textContent=summaries(selectedBrands,'brands');
      $('[data-scb-width-summary]').textContent=summaries(selectedWidths,'widths');
      const format=(set,all,suffix)=>set.size===0?all:set.size===1?[...set][0]+suffix:`${set.size} selected`;
      $('[data-scb-wheel-size-summary]').textContent=format(selectedWheelSizes,'All sizes','mm');
      $('[data-scb-wheel-hardness-summary]').textContent=format(selectedWheelHardnesses,'All hardnesses','');
      $('[data-scb-truck-fit-summary]').textContent=format(selectedTruckFits,'All widths','"');
    }
    function openModal(kind){
      modalKind=kind;lastFocus=document.activeElement;search.value='';sort.value=kind==='decks'?'newest':'featured';selectedBrands.clear();selectedWidths.clear();selectedWheelSizes.clear();selectedWheelHardnesses.clear();selectedTruckFits.clear();
      visibleProductCount=productPageSize;
      root.querySelectorAll('.scb__multi-filter').forEach(d=>d.open=false);
      $('[data-scb-modal-title]').textContent=kind==='decks'?'Choose your deck':'Choose '+labels[kind].toLowerCase();
      modal.hidden=false;document.body.classList.add('scb__modal-open');renderFilterOptions();renderProducts();
      if(kind==='decks')loadDecks();else loadComponents(kind);modal.querySelector('.scb__modal-panel').focus();
    }
    function renderProducts(){
      if(modalKind==='decks'&&!decksLoaded){
        results.replaceChildren(node('p','scb__empty',deckLoadError||'Loading available decks from Shopify, newest first…'));
        $('[data-scb-count]').textContent=deckLoadError?'Catalog unavailable':'Loading deck catalog…';
        return;
      }
      const kind=modalKind,query=search.value.trim().toLowerCase();let products=all(kind).filter(p=>(!selectedBrands.size||selectedBrands.has((p.vendor||'').trim()))&& (kind!=='decks'||!selectedWidths.size||selectedWidths.has(widthKey(deckWidth(p))))&&(kind!=='wheels'||!selectedWheelSizes.size||selectedWheelSizes.has(widthKey(p.wheel)))&&(kind!=='wheels'||!selectedWheelHardnesses.size||selectedWheelHardnesses.has(String(p.hardness??'').trim().toUpperCase()))&&(kind!=='trucks'||!selectedTruckFits.size||selectedTruckFits.has(widthKey(p.fit)))&&(!query||(p.title+' '+p.vendor).toLowerCase().includes(query)));
      if(kind==='decks')products=products.filter(p=>p.available&&p.price>0);
      if(kind==='decks'&&sort.value==='newest'){}else if(sort.value==='price-asc')products.sort((a,b)=>a.price-b.price);else if(sort.value==='price-desc')products.sort((a,b)=>b.price-a.price);else if(sort.value==='name')products.sort((a,b)=>a.title.localeCompare(b.title));else if(kind!=='decks')products.sort((a,b)=>popularity(b,kind)-popularity(a,kind)||a.price-b.price);
      results.replaceChildren();const shown=Math.min(visibleProductCount,products.length);$('[data-scb-count]').textContent=`Showing ${shown} of ${products.length}${kind==='decks'?' available decks':' available options'}`;
      if(!products.length){const empty=node('div','scb__empty');empty.append(node('p','','No matching products. Try clearing the search or filters.'));const clear=node('button','scb__clear-all','Clear all filters');clear.type='button';clear.addEventListener('click',clearAllFilters);empty.append(clear);results.append(empty);return;}
      products.slice(0,visibleProductCount).forEach(p=>{const active=String(p.id)===String(kind==='decks'?deck?.id:selected[kind]);const b=node('button','scb__product'+(active?' is-selected':''));b.type='button';b.setAttribute('aria-pressed',String(active));b.append(img(p,'',360),node('span','scb__product-title',p.title));if(active)b.append(node('span','scb__selected-badge','Selected'));const price=node('span','scb__product-price',priced(p));if(kind!=='decks'){const existing=current(kind);if(existing&&existing.id!==p.id){const delta=p.price-existing.price;price.textContent+=` · ${delta===0?'same price':`${delta>0?'+':'−'}${usd(Math.abs(delta))}`}`;}}b.append(price);if(kind==='trucks'&&deck&&number(p.fit)!==null&&deckWidth(deck)!==null&&Math.abs(number(p.fit)-deckWidth(deck))>0.13)b.append(node('span','scb__product-detail','Outside typical deck width match'));if(kind==='trucks')b.append(node('span','scb__product-detail',`Fits ${p.fit||'?'}" decks`));if(kind==='risers')b.append(node('span','scb__product-detail',thickness(p)!==null?`Thickness: ${Math.round(thickness(p)*25.4*10)/10}mm`:'Check riser thickness'));if(kind==='wheels')b.append(node('span','scb__product-detail',`${p.wheel||'?'} mm · ${p.hardness||'Hardness not specified'}`));b.addEventListener('click',()=>{if(kind==='decks'){deckSelect.value=String(p.id);for(const k of components){selected[k]=null;manual[k]=false;}refresh();}else{selected[kind]=p.id;manual[kind]=true;if(kind==='wheels'||kind==='risers')reconcileWheelDependencies();render();}closeModal();});results.append(b);});
      if(shown<products.length){const more=node('button','scb__show-more',`Show ${Math.min(productPageSize,products.length-shown)} more`);more.type='button';more.addEventListener('click',()=>{visibleProductCount+=productPageSize;renderProducts();});results.append(more);}
    }
    function clearAllFilters(){search.value='';selectedBrands.clear();selectedWidths.clear();selectedWheelSizes.clear();selectedWheelHardnesses.clear();selectedTruckFits.clear();visibleProductCount=productPageSize;renderFilterOptions();renderProducts();}
    function updateDelivery(){
      delivery=root.querySelector('[data-scb-delivery]:checked')?.value||'shipping';
      const pickup=delivery==='pickup';
      $('[data-scb-delivery-note]').textContent=pickup
        ?'Your complete is ready for pickup unassembled.'
        :'Your complete ships unassembled.';
      $('[data-scb-fineprint]').textContent=pickup
        ?'Your complete will be ready for pickup unassembled. Complimentary assembly is available upon request when you arrive.'
        :'Your complete ships unassembled.';
      refresh();
      if(!modal.hidden){renderFilterOptions();renderProducts();}
    }
    root.querySelectorAll('[data-scb-delivery]').forEach(radio=>radio.addEventListener('change',updateDelivery));
    $('[data-scb-deck-button]').addEventListener('click',()=>openModal('decks'));
    root.querySelectorAll('[data-scb-close]').forEach(el=>el.addEventListener('click',closeModal));
    modal.addEventListener('keydown',e=>{if(e.key==='Escape')closeModal();if(e.key==='Tab'){const els=[...modal.querySelectorAll('button:not(:disabled),input:not(:disabled),select:not(:disabled)')];if(els.length){const first=els[0],last=els[els.length-1];if(e.shiftKey&&document.activeElement===first){e.preventDefault();last.focus();}else if(!e.shiftKey&&document.activeElement===last){e.preventDefault();first.focus();}}}});
    const resetAndRenderProducts=()=>{visibleProductCount=productPageSize;renderProducts();};
    search.addEventListener('input',resetAndRenderProducts);sort.addEventListener('change',resetAndRenderProducts);
    $('[data-scb-clear-brand]').addEventListener('click',()=>{selectedBrands.clear();visibleProductCount=productPageSize;renderFilterOptions();renderProducts();});
    $('[data-scb-clear-width]').addEventListener('click',()=>{selectedWidths.clear();visibleProductCount=productPageSize;renderFilterOptions();renderProducts();});
    for(const [key,set] of [['wheel-size',selectedWheelSizes],['wheel-hardness',selectedWheelHardnesses],['truck-fit',selectedTruckFits]]){
      $(`[data-scb-clear-${key}]`).addEventListener('click',()=>{set.clear();visibleProductCount=productPageSize;renderFilterOptions();renderProducts();});
    }
    // Keep one checklist open at a time and dismiss when clicking elsewhere.
    const filterDetails=[...root.querySelectorAll('.scb__multi-filter')];
    filterDetails.forEach(detail=>detail.addEventListener('toggle',()=>{if(detail.open)filterDetails.forEach(other=>{if(other!==detail)other.open=false;});}));
    document.addEventListener('click',e=>{if(!modal.hidden&&!filterDetails.some(d=>d.contains(e.target)))filterDetails.forEach(d=>d.open=false);});
    add.addEventListener('click',async()=>{error.hidden=true;const items=[deck,...components.map(current)].filter(Boolean);if(!deck||!required.every(k=>current(k))||(delivery==='pickup'&&selectedPickupIssues().length))return;add.disabled=true;const buildId='select-'+Date.now().toString(36)+'-'+Math.random().toString(36).slice(2,8);try{const rootUrl=window.Shopify?.routes?.root||'/';await addCompleteToCart(items,buildId,delivery,rootUrl);window.location.assign(rootUrl+'cart');}catch(e){error.textContent=e.message;error.hidden=false;add.disabled=false;}});
    refresh();
    // Fetch the linked deck directly; the full deck catalog remains lazy.
    if(params.get('deck')||params.get('variant'))loadLinkedDeck();
    // Hold recommendations and purchasing until every complete catalog has settled.
    // This prevents prices and selected parts from changing during background loading.
    Promise.allSettled(kindKeys.map(loadComponents)).then(()=>{recommendationsReady=true;refresh();});
  });
})();
